import { useMemo, useRef } from "react";
import type { Item } from "../App";
import { normalizeSerialKey } from "../services/serialIndex";
import { computeStateSort, toEpochMillis } from "../lib/inventoryHelpers";

type RowState = "open" | "ready" | "invoiced";

const getRowState = (it: Item): RowState => {
  if ((it as any).invoiceReportId) return "invoiced";
  if (it.completed) return "ready";
  return "open";
};

const cmpDateDesc = (a?: any, b?: any) =>
  String(b || "").localeCompare(String(a || ""));

export function useVisibleItems(
  items: Item[],
  filters: Record<string, string>,
  customerNameById: Record<string, string>,
  customerIdFilter?: string | null,
  currentUser?: string | null
) {
  const stableDeleteOrderRef = useRef<string[]>([]);

  return useMemo<Item[]>(() => {
    const q = (s?: string) => (s || "").trim().toLowerCase();
    const fOrd = q(filters.orderNumber);
    const fMan = q(filters.manufacturer);
    const fMod = q(filters.model);
    const fCust = q(filters.customer);
    const fAda = q(filters.chargerIncluded);
    const fBy = q(filters.createdBy);
    const fSerKey = normalizeSerialKey(filters.serial || "");

    const filtered = items.filter((it) => {
      if ((it as any).isDraft === true) return false;
      if (customerIdFilter && it.customerId !== customerIdFilter) return false;

      const byText = (it.createdBy || (it as any).initials || "").toString();
      const adaText = it.chargerIncluded ? "ja yes true 1" : "nej no false 0";
      const serialMatch =
        !fSerKey || normalizeSerialKey(it.serial || "").includes(fSerKey);

      return (
        (!fOrd || q(it.orderNumber).includes(fOrd)) &&
        (!fMan || q(it.manufacturer).includes(fMan)) &&
        (!fMod || q(it.model).includes(fMod)) &&
        (!fCust || q(it.customer || customerNameById[it.customerId || ""]).includes(fCust)) &&
        serialMatch &&
        (!fAda || adaText.includes(fAda)) &&
        (!fBy || q(byText).includes(fBy))
      );
    });

    const toSortKey = (it: Item) => {
      const stateSort = (it as any).stateSort ?? computeStateSort(it.completed, (it as any).invoiceReportId);
      const ts = toEpochMillis((it as any).updatedAt ?? it.completedAt ?? it.createdAt);
      return { stateSort, ts };
    };

    const sorted = [...filtered].sort((a, b) => {
      const ak = toSortKey(a);
      const bk = toSortKey(b);
      if (ak.stateSort !== bk.stateSort) return ak.stateSort - bk.stateSort;
      return bk.ts - ak.ts; // desc by timestamp within state
    });

    const hasOwnDeleteSelection = items.some((it) => {
      if (!(it as any).deletePending) return false;
      const markedBy = (it as any).deleteMarkedBy;
      return !markedBy || markedBy === currentUser;
    });

    if (!hasOwnDeleteSelection) {
      stableDeleteOrderRef.current = sorted.map((it) => it.id);
      return sorted;
    }

    if (stableDeleteOrderRef.current.length === 0) {
      stableDeleteOrderRef.current = sorted.map((it) => it.id);
    }

    const knownIds = new Set(stableDeleteOrderRef.current);
    sorted.forEach((it) => {
      if (!knownIds.has(it.id)) {
        stableDeleteOrderRef.current.push(it.id);
        knownIds.add(it.id);
      }
    });

    const stableRank = new Map(stableDeleteOrderRef.current.map((id, index) => [id, index]));
    return [...sorted].sort((a, b) =>
      (stableRank.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
      (stableRank.get(b.id) ?? Number.MAX_SAFE_INTEGER)
    );
  }, [items, filters, customerNameById, customerIdFilter, currentUser]);
}
